import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import React from 'react';
import { CheckmarkOutline, DuplicateOutline, FilterOutline, HelpCircleOutline, SparkSolid, UndoOutline } from '../../../icons/icons';
import { UDViewPlaneFilterSettingModel } from '../../../udView/udview';
import { EventBus } from '../../contexts/event-bus';
import { HomeFlyoutTypes } from '../../features/home/flyouts/types';
import { AiChangeSummary } from './AiChangeSummary';
import { AiMarkdown } from './AiMarkdown';
import { AiChatMessage, revertChanges } from './ai-chat-helpers';

function CopyButton(props: { text: string }) {
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef<number | undefined>(undefined);
    React.useEffect(() => () => window.clearTimeout(timer.current), []);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(props.text);
            setCopied(true);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard unavailable; nothing useful to show
        }
    };
    return (
        <Button
            fillMode="flat"
            size="small"
            className="ai-chat-copy-btn"
            svgIcon={copied ? CheckmarkOutline : DuplicateOutline}
            title={copied ? 'Copied' : 'Copy'}
            aria-label="Copy message"
            onClick={copy}
        />
    );
}

export interface AiMessageProps {
    message: AiChatMessage;
    // The message that followed this one, used to show which suggestion was chosen.
    next?: AiChatMessage;
    isLast: boolean;
    loading: boolean;
    filters?: UDViewPlaneFilterSettingModel[];
    onAsk: (text: string) => void;
    onRevert: (id: number) => void;
}

export function AiMessage(props: AiMessageProps) {
    const { message: m, next, isLast, loading, filters } = props;

    if (m.role === 'user') {
        return (
            <div className="ai-chat-message ai-chat-message-user">
                <CopyButton text={m.text} />
                <div className="ai-chat-prompt">{m.text}</div>
            </div>
        );
    }

    const canRevert = !!m.changes && !!filters && revertChanges(filters, m.changes) !== null;
    const isClarification = m.kind === 'clarification';
    const chosen = next?.role === 'user' ? next.text : undefined;
    const answered = !isLast || loading;

    return (
        <div className="ai-chat-message ai-chat-message-assistant">
            <span className="ai-chat-avatar" aria-hidden="true">
                <SvgIcon icon={SparkSolid} size="small" />
            </span>
            <div className="ai-chat-assistant-body d-flex flex-column gap-2">
                <div className="d-flex align-items-start gap-1">
                    <div className={`ai-chat-reply ${isClarification ? 'ai-chat-reply-question' : ''}`}>
                        {isClarification && <SvgIcon icon={HelpCircleOutline} size="small" className="ai-chat-question-icon" />}
                        <AiMarkdown text={m.text} />
                    </div>
                    <CopyButton text={m.text} />
                </div>

                {isClarification && !!m.clarification?.suggestedAnswers.length && (
                    <div className="d-flex flex-wrap gap-1">
                        {m.clarification.suggestedAnswers.map((answer) => {
                            const isChosen = answered && chosen === answer;
                            return (
                                <button
                                    key={answer}
                                    type="button"
                                    className={`ai-chat-chip ${isChosen ? 'ai-chat-chip-chosen' : ''}`}
                                    disabled={answered}
                                    onClick={() => props.onAsk(answer)}>
                                    {isChosen && <SvgIcon icon={CheckmarkOutline} size="small" />}
                                    {answer}
                                </button>
                            );
                        })}
                    </div>
                )}

                {m.changes && (
                    <>
                        <AiChangeSummary changes={m.changes} reverted={m.reverted} />
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Button
                                fillMode="outline"
                                className="button-small"
                                svgIcon={FilterOutline}
                                onClick={() => EventBus.fire('open-flyout', { type: HomeFlyoutTypes.filters })}>
                                Open filters
                            </Button>
                            {m.reverted ? (
                                <span className="ai-chat-reverted-badge d-flex align-items-center gap-1">
                                    <SvgIcon icon={CheckmarkOutline} size="small" />
                                    Reverted
                                </span>
                            ) : (
                                <Button
                                    fillMode="outline"
                                    className="button-small"
                                    svgIcon={UndoOutline}
                                    disabled={!canRevert || loading}
                                    title={canRevert ? 'Undo this change' : 'Filters were changed since, cannot revert'}
                                    onClick={() => props.onRevert(m.id)}>
                                    Revert
                                </Button>
                            )}
                            {!m.reverted && !canRevert && <span className="secondary-text-color ai-chat-hint">Filters changed since</span>}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
